import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useLang } from '../i18n/context';
import { LANGS } from '../i18n';
import { stripLang } from '../i18n';
import { track } from '../lib/track';
import { WaIcon } from './CTAs';
import { waLink } from '../lib/site';
import Logo from './Logo';

function Caret() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function Dropdown({ label, items, base = '', badge }) {
    return (
        <div className="group relative">
            <button
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}-dropdown`}
                className="flex items-center gap-1 whitespace-nowrap py-2 text-[13px] font-medium text-ink-body transition-colors group-hover:text-gold-700"
            >
                {label} {badge && <span className="ml-1 rounded-full bg-peri-300 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-gold-700">{badge}</span>}
                <Caret />
            </button>
            <div className="invisible absolute left-0 top-full z-40 w-72 translate-y-1 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                <div className="mt-1 overflow-hidden rounded-2xl border border-peri-200 bg-white shadow-card-hover">
                    {items.map((it) => (
                        <Link
                            key={it.slug || it.path}
                            to={base && it.slug ? `${base}/${it.slug}` : `${base}${it.path || ''}`}
                            className="block border-b border-peri-100 px-5 py-3.5 last:border-0 hover:bg-peri-50"
                        >
                            <span className="block text-sm font-semibold text-ink">{it.title}</span>
                            <span className="mt-0.5 block text-xs text-ink-subtle">{it.desc}</span>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}

const ECO_MENU = [
    { path: '/', title: 'Home — iINSPIRE', desc: 'One ecosystem. Three worlds.' },
    { path: '/dental', title: 'Dental', desc: 'You are here — Klinik Pergigian Irma Ariandi' },
    { path: '/consulting', title: 'Consulting', desc: 'Growth. Strategy. Impact.' },
    { path: '/creative', title: 'Creative', desc: 'Books. Writing. Ideas. Expressions.' },
    { path: '/coffee-with-dr-irma', title: 'Coffee With Dr. Irma', desc: 'The editorial voice' },
];

export default function Navbar() {
    const { lang, t, path } = useLang();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const P = `/dental/${lang}`;

    const switchLang = (code) => `/dental/${code}${path === '/' ? '' : path}`;
    const goLang = (code) => {
        track('language_select', { language: code });
        navigate(switchLang(code));
        setOpen(false);
    };

    const flatLinks = [
        { to: `${P}/teen-smile`, label: t.nav.teen, badge: t.nav.teenBadge },
        { to: `${P}/kolum`, label: t.nav.kolum },
        { to: `${P}/patient-stories`, label: t.nav.stories },
        { to: `${P}/contact`, label: t.nav.contact },
    ];

    return (
        <header className="fixed inset-x-0 top-0 z-50 border-b border-peri-200/60 bg-white/85 backdrop-blur-md">
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:h-[72px] lg:px-12">
                <Link to="/" data-testid="nav-home-link" aria-label="iINSPIRE Home" title="iINSPIRE Home">
                    <Logo />
                </Link>

                <nav className="hidden items-center gap-4 lg:flex xl:gap-5">
                    <Dropdown label="iINSPIRE" items={ECO_MENU} base="" />
                    <Dropdown label={t.nav.treatments} items={t.nav.treatmentsMenu} base={`${P}/treatments`} />
                    <Dropdown label={t.nav.international} items={t.nav.internationalMenu} base={P} />
                    <Dropdown label={t.nav.doctors} items={t.nav.doctorsMenu} base={P} />
                    <Link to={`${P}${t.nav.digitalLink.path}`} data-testid="nav-digital-link" className="whitespace-nowrap py-2 text-[13px] font-medium text-ink-body transition-colors hover:text-gold-700">
                        {t.nav.digitalLink.title}
                    </Link>
                    {flatLinks.slice(0, 2).map((l) => (
                        <Link key={l.to} to={l.to} className="whitespace-nowrap py-2 text-[13px] font-medium text-ink-body transition-colors hover:text-gold-700">
                            {l.label}
                            {l.badge && <span className="ml-1.5 inline-block whitespace-nowrap rounded-full bg-peri-300 px-1.5 py-0.5 align-middle text-[8px] uppercase tracking-wider text-gold-700">{l.badge}</span>}
                        </Link>
                    ))}
                    <Link to={`${P}/contact`} className="whitespace-nowrap py-2 text-[13px] font-medium text-ink-body transition-colors hover:text-gold-700">
                        {t.nav.contact}
                    </Link>
                </nav>

                <div className="flex items-center gap-3">
                    <div className="hidden items-center rounded-full border border-peri-300 bg-peri-50 p-0.5 sm:flex" data-testid="language-selector">
                        {LANGS.map((l) => (
                            <button
                                key={l.code}
                                onClick={() => goLang(l.code)}
                                className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${l.code === lang ? 'bg-gold-400 text-ink' : 'text-ink-muted hover:text-gold-700'}`}
                                data-testid={`lang-switch-${l.code}`}
                            >
                                {l.label}
                            </button>
                        ))}
                    </div>
                    <a
                        href={waLink('')}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-whatsapp-cta"
                        className="hidden items-center gap-2 rounded-full bg-gold-400 px-4 py-2 text-[13px] font-semibold text-ink transition-all hover:bg-gold-500 md:inline-flex"
                    >
                        <WaIcon className="h-3.5 w-3.5" />
                        {t.cta.whatsapp}
                    </a>
                    <button
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-peri-300 text-ink lg:hidden"
                        onClick={() => setOpen(!open)}
                        aria-label={open ? t.nav.close : t.nav.menu}
                        data-testid="nav-mobile-toggle"
                    >
                        {open ? (
                            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                        ) : (
                            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none"><path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                        )}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden border-t border-peri-200 bg-white lg:hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="space-y-1 px-4 py-4">
                            <Link to="/" onClick={() => setOpen(false)} className="block rounded-xl px-3 py-2.5 font-semibold text-ink hover:bg-peri-50" data-testid="mobile-eco-home">iINSPIRE Home</Link>
                            <div className="flex gap-2 px-3 pb-1 pt-1">
                                <Link to="/consulting" onClick={() => setOpen(false)} className="rounded-full border border-peri-300 px-3 py-1.5 text-[11px] font-semibold text-ink-body hover:bg-peri-50" data-testid="mobile-eco-consulting">Consulting</Link>
                                <Link to="/creative" onClick={() => setOpen(false)} className="rounded-full border border-peri-300 px-3 py-1.5 text-[11px] font-semibold text-ink-body hover:bg-peri-50" data-testid="mobile-eco-creative">Creative</Link>
                                <Link to="/coffee-with-dr-irma" onClick={() => setOpen(false)} className="rounded-full border border-peri-300 px-3 py-1.5 text-[11px] font-semibold text-ink-body hover:bg-peri-50" data-testid="mobile-eco-coffee">Coffee</Link>
                            </div>
                            <div className="my-2 border-t border-peri-200" />
                            <Link to={P} onClick={() => setOpen(false)} className="block rounded-xl px-3 py-2.5 font-medium text-ink hover:bg-peri-50">{t.nav.home} · Dental</Link>
                            <p className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold-700">{t.nav.treatments}</p>
                            {t.nav.treatmentsMenu.map((it) => (
                                <Link key={it.slug} to={`${P}/treatments/${it.slug}`} onClick={() => setOpen(false)} className="block rounded-xl px-5 py-2 text-sm text-ink-body hover:bg-peri-50">{it.title}</Link>
                            ))}
                            <Link to={`${P}${t.nav.digitalLink.path}`} onClick={() => setOpen(false)} className="block rounded-xl px-5 py-2 text-sm text-ink-body hover:bg-peri-50">{t.nav.digitalLink.title}</Link>
                            <p className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold-700">{t.nav.international}</p>
                            {t.nav.internationalMenu.map((it) => (
                                <Link key={it.path} to={`${P}${it.path}`} onClick={() => setOpen(false)} className="block rounded-xl px-5 py-2 text-sm text-ink-body hover:bg-peri-50">{it.title}</Link>
                            ))}
                            <p className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold-700">{t.nav.doctors}</p>
                            {t.nav.doctorsMenu.map((it) => (
                                <Link key={it.path} to={`${P}${it.path}`} onClick={() => setOpen(false)} className="block rounded-xl px-5 py-2 text-sm text-ink-body hover:bg-peri-50">{it.title}</Link>
                            ))}
                            {flatLinks.map((l) => (
                                <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block rounded-xl px-3 py-2.5 font-medium text-ink hover:bg-peri-50">
                                    {l.label}
                                    {l.badge && <span className="ml-2 rounded-full bg-peri-300 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-gold-700">{l.badge}</span>}
                                </Link>
                            ))}
                            <Link to={`${P}/about`} onClick={() => setOpen(false)} className="block rounded-xl px-3 py-2.5 font-medium text-ink hover:bg-peri-50">About</Link>
                            <div className="flex gap-2 pt-3">
                                {LANGS.map((l) => (
                                    <button
                                        key={l.code}
                                        onClick={() => goLang(l.code)}
                                        className={`flex-1 rounded-xl border px-2 py-2 text-xs font-semibold ${l.code === lang ? 'border-gold-500 bg-gold-400 text-ink' : 'border-peri-300 text-ink-muted'}`}
                                        data-testid={`lang-switch-mobile-${l.code}`}
                                    >
                                        {l.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
